import { useCallback } from 'react';

// Define types locally to avoid import errors
interface ZmanimData {
  shabbat_start?: string;
  shabbat_end?: string;
  sunrise?: string;
  sunset?: string;
  chatzot?: string;
  alot_hashachar?: string;
  tzeit_hakochavim?: string;
  [key: string]: string | undefined;
}

// Default constants for Hadera, Israel
const HADERA_COORDINATES = {
  lat: 32.4340,
  lng: 34.9197,
  city: "Hadera",
  country: "IL",
  tzid: "Asia/Jerusalem"
};

interface UserLocation {
  lat: number;
  lng: number;
  name?: string;
  city?: string;
  country?: string;
  tzid?: string;
}

interface TimeItem {
  id: string;
  title: string;
  time: string;
  prayer_type?: string;
  is_relative?: boolean;
  relative_to?: string;
  offset_minutes?: number;
  show_time?: boolean;
  calculatedTime?: string;
}

interface CachedData {
  data: ZmanimData;
  timestamp: number;
  location: {
    city: string;
    country: string;
    lat: number;
    lng: number;
  };
}

interface ProcessedTimesCache {
  pageId: string;
  displayTimes: TimeItem[];
  timestamp: number;
}

const CACHE_KEY = 'zmanim_cache';
const PROCESSED_TIMES_CACHE_KEY = 'processed_times_cache';
const CACHE_TTL = 3600000; // 1 hour in milliseconds
const CACHE_EMERGENCY_TTL = 86400000; // 24 hours in milliseconds - for when API fails

export const useZmanimCache = () => {
  const getCachedData = useCallback((): CachedData | null => {
    try {
      const cachedJSON = localStorage.getItem(CACHE_KEY);
      if (!cachedJSON) return null;
      
      const cached = JSON.parse(cachedJSON) as CachedData;
      const now = new Date().getTime();
      
      // Check if cache is expired
      if (now - cached.timestamp > CACHE_TTL) {
        // Don't remove immediately - we'll keep it for emergency use
        // but mark it as stale by returning null
        return null;
      }
      
      return cached;
    } catch (error) {
      console.error('Error reading zmanim cache:', error);
      return null;
    }
  }, []);

  // Get emergency cache data (even if expired, up to 24 hours)
  const getEmergencyCachedData = useCallback((): CachedData | null => {
    try {
      const cachedJSON = localStorage.getItem(CACHE_KEY);
      if (!cachedJSON) return null;
      
      const cached = JSON.parse(cachedJSON) as CachedData;
      const now = new Date().getTime();
      
      // Only return if within emergency TTL (24 hours)
      if (now - cached.timestamp > CACHE_EMERGENCY_TTL) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }
      
      return cached;
    } catch (error) {
      console.error('Error reading emergency zmanim cache:', error);
      return null;
    }
  }, []);

  const setCachedData = useCallback((data: ZmanimData, location: UserLocation): void => {
    try {
      // Only cache if we have at least some data
      if (Object.keys(data).length === 0) {
        console.warn('Not caching empty zmanim data');
        return;
      }
      
      const cacheData: CachedData = {
        data,
        timestamp: new Date().getTime(),
        location: {
          city: location.city || '',
          country: location.country || '',
          lat: location.lat,
          lng: location.lng
        }
      };
      
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error setting zmanim cache:', error);
    }
  }, []);

  const isCacheValid = useCallback((location: UserLocation): boolean => {
    const cached = getCachedData();
    if (!cached) return false;
    
    // Check if location matches
    return (
      cached.location.city === location.city &&
      cached.location.country === location.country &&
      Math.abs(cached.location.lat - location.lat) < 0.001 &&
      Math.abs(cached.location.lng - location.lng) < 0.001
    );
  }, [getCachedData]);

  // New functions for processed times cache
  const getProcessedTimesCache = useCallback((pageId: string): TimeItem[] | null => {
    try {
      const cachedJSON = localStorage.getItem(PROCESSED_TIMES_CACHE_KEY);
      if (!cachedJSON) return null;
      
      const allCachedTimes = JSON.parse(cachedJSON) as ProcessedTimesCache[];
      const now = new Date().getTime();
      
      // Find the cache for this specific page
      const pageCache = allCachedTimes.find(cache => cache.pageId === pageId);
      if (!pageCache) return null;
      
      // Check if cache is expired
      if (now - pageCache.timestamp > CACHE_TTL) {
        return null;
      }
      
      return pageCache.displayTimes;
    } catch (error) {
      console.error('Error reading processed times cache:', error);
      return null;
    }
  }, []);

  const setProcessedTimesCache = useCallback((pageId: string, displayTimes: TimeItem[]): void => {
    try {
      // Only cache if we have data
      if (!displayTimes.length) {
        return;
      }
      
      // Get existing cache
      let allCachedTimes: ProcessedTimesCache[] = [];
      const cachedJSON = localStorage.getItem(PROCESSED_TIMES_CACHE_KEY);
      
      if (cachedJSON) {
        allCachedTimes = JSON.parse(cachedJSON) as ProcessedTimesCache[];
        // Remove existing entry for this page if it exists
        allCachedTimes = allCachedTimes.filter(cache => cache.pageId !== pageId);
      }
      
      // Add new cache entry
      allCachedTimes.push({
        pageId,
        displayTimes,
        timestamp: new Date().getTime()
      });
      
      // Store back in localStorage
      localStorage.setItem(PROCESSED_TIMES_CACHE_KEY, JSON.stringify(allCachedTimes));
      console.log(`Cached processed times for page ${pageId}`);
    } catch (error) {
      console.error('Error setting processed times cache:', error);
    }
  }, []);

  return {
    getCachedData,
    getEmergencyCachedData,
    setCachedData,
    isCacheValid,
    getProcessedTimesCache,
    setProcessedTimesCache
  };
}; 